import type { Team } from "../schemas.js";
import type { Game, Side } from "./data.js";
import { href } from "./router.js";

/** Small HTML-string building blocks shared by the views. */

export const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

export const fmt1 = (n: number): string => n.toFixed(1);

/** "🌯 Burrito Union" -> { emoji: "🌯", text: "Burrito Union" }; emoji is "" when there isn't one. */
export function splitLeadingEmoji(name: string): { emoji: string; text: string } {
  const m = name.match(/^(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*)\s*/u);
  return m ? { emoji: m[1]!, text: name.slice(m[0].length) } : { emoji: "", text: name };
}

/** An emoji as an SVG data-URI favicon. */
export const emojiFavicon = (emoji: string): string =>
  `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><text y='14' font-size='14'>${emoji}</text></svg>`;

/** A number with an explicit + or − sign (− is a real minus glyph); no sign on 0. */
export const signed = (n: number): string => `${n > 0 ? "+" : n < 0 ? "−" : ""}${fmt1(Math.abs(n))}`;

/**
 * Value coloring: a two-tone red→green ramp — deep then bright red for the bad
 * end, bright then deep green for the good end, with the red↔green crossover
 * squeezed into a narrow middle band. So most values land as clearly red or
 * clearly green (dodging the washed-out orange/yellow midtones) while the two
 * steps per side still show *how* bad or good. Color is always *redundant* — the
 * number is right there — so it stays meaningful for color-vision deficiencies
 * too. Every stop clears 3:1 contrast on the dark surface, green ≈ brand accent.
 */
type RGB = readonly [number, number, number];
const RAMP_STOPS: readonly { at: number; rgb: RGB }[] = [
  { at: 0.0, rgb: [225, 29, 46] }, // #e11d2e deep red — worst
  { at: 0.47, rgb: [242, 100, 109] }, // #f2646d red
  { at: 0.53, rgb: [87, 211, 166] }, // #57d3a6 green
  { at: 1.0, rgb: [22, 184, 102] }, // #16b866 deep green — best
];
const mix = (a: RGB, b: RGB, t: number): string =>
  `rgb(${a.map((c, i) => Math.round(c + (b[i]! - c) * t)).join(",")})`;

/** t in [0,1]: red (bad) → green (good), with a compressed crossover (clamped). */
export function rampColor(t: number): string {
  const x = Math.max(0, Math.min(1, t));
  let i = 0;
  while (i < RAMP_STOPS.length - 2 && x > RAMP_STOPS[i + 1]!.at) i++;
  const a = RAMP_STOPS[i]!;
  const b = RAMP_STOPS[i + 1]!;
  return mix(a.rgb, b.rgb, (x - a.at) / (b.at - a.at));
}

// Per-metric placement on the ramp. Efficiency is on an absolute 70–100% band;
// bench points are red the further above zero they climb (0 = you played your
// best, full green); luck is diverging, so a score of 0 sits on the neutral
// midpoint and the poles scale to the biggest swing in the league.
export const effColor = (efficiency: number): string => rampColor((efficiency - 0.7) / 0.3);
export const wastedColor = (points: number, maxPoints: number): string =>
  rampColor(maxPoints > 0 ? 1 - points / maxPoints : 1);
export const luckColor = (luck: number, maxAbsLuck: number): string =>
  rampColor(maxAbsLuck > 0 ? 0.5 + 0.5 * (luck / maxAbsLuck) : 0.5);

/**
 * Points left on the bench; `color` places it on the red→green ramp. The value
 * is always positive, so `plus` (the leading "+") is droppable where a whole
 * column of them would just be noise, e.g. the compact standings.
 */
export const missedText = (points: number, color?: string, plus = true): string =>
  `<span class="missed-text"${color ? ` style="color:${color}"` : ""} title="Points left on the bench">${plus ? "+" : ""}${fmt1(points)}</span>`;

/**
 * Manager efficiency: the share of the best-possible lineup the manager actually
 * started, as a whole-number percent. `color` places it on the red→green ramp;
 * `unit` (the trailing "%") is droppable in a labelled column.
 */
export const effText = (efficiency: number, color?: string, unit = true): string => {
  const pct = Math.round(efficiency * 100);
  return `<span class="eff-text"${color ? ` style="color:${color}"` : ""} title="Started ${pct}% of the best-possible lineup">${pct}${unit ? "%" : ""}</span>`;
};

/**
 * Luck score: positive = luckier than the field (sloppy opponents and/or wins
 * the best lineups would take away), negative = unluckier. `color` places it on
 * the red→green ramp (red = unlucky, green = lucky).
 */
export const luckText = (luck: number, color?: string): string => {
  const mood = luck > 0 ? "Fortunate on balance" : luck < 0 ? "Unlucky on balance" : "Nothing notable";
  return `<span class="luck-text"${color ? ` style="color:${color}"` : ""} title="${mood}: beatings dodged in games you won, plus the margin of any game the best lineups would flip">${signed(luck)}</span>`;
};

/**
 * How many places a team differs between the actual and optimal standings: a
 * green up arrow when best lineups would rank it higher, red down when lower,
 * nothing when it wouldn't move. `delta` is actualRank − optimalRank.
 */
export const moveText = (delta: number): string => {
  if (delta === 0) return "";
  const up = delta > 0;
  const n = Math.abs(delta);
  const title = `${up ? "Up" : "Down"} ${n} ${n === 1 ? "place" : "places"} in the optimal standings`;
  return `<span class="move ${up ? "move-up" : "move-down"}" title="${title}">${up ? "▲" : "▼"}${n}</span>`;
};

export const teamLink = (team: Team, withManager = true): string =>
  `<a class="team-link" href="${href.team(team.teamId)}">${esc(team.name)}</a>${
    withManager ? `\n   <span class="mgr">${esc(team.manager)}</span>` : ""
  }`;

/**
 * Segmented control. `name` is the data attribute the page listens for, e.g.
 * name "standings" renders buttons with data-standings="actual".
 */
export function segmented(
  name: string,
  label: string,
  options: ReadonlyArray<readonly [value: string, text: string]>,
  current: string,
  extraClass = "",
): string {
  const buttons = options
    .map(
      ([value, text]) =>
        `<button type="button" data-${name}="${value}" aria-pressed="${value === current}">${esc(text)}</button>`,
    )
    .join("");
  return `<div class="segmented ${extraClass}" role="group" aria-label="${esc(label)}">${buttons}</div>`;
}

export function pressSegment(name: string, value: string): void {
  document
    .querySelectorAll<HTMLButtonElement>(`button[data-${name}]`)
    .forEach((b) => b.setAttribute("aria-pressed", String(b.dataset[name] === value)));
}

/**
 * One game as a compact row, mirrored around "vs": big actual score, and under
 * it the best-lineup score (bright if it wins that way) with the points missed
 * in small print. With `link`, the whole row opens the matchup view.
 */
export function gameRow(game: Game, link = true): string {
  const { a, b, flips } = game;
  const score = (s: Side, opp: Side, area: "sa" | "sb") => `
    <div class="g-score ${area}">
      <span class="g-actual${game.final && s.actual > opp.actual ? " win" : ""}"><small>Started</small> ${fmt1(s.actual)}</span>
      <span class="g-sub"><span class="g-opt ${!game.final || s.optimal === opp.optimal ? "" : s.optimal > opp.optimal ? "win" : "loss"}"><small>Best</small> ${fmt1(
        s.optimal,
      )}</span><span class="g-missed"><small>Missed</small> ${missedText(s.missed, undefined, false)}</span></span>
    </div>`;
  const team = (s: Side, area: "ta" | "tb") => `<div class="g-team ${area}">${teamLink(s.team, false)}</div>`;
  const label = `Week ${game.week}: ${a.team.name} vs ${b.team.name}`;
  return `
    <div class="game${link ? " linked" : ""}${flips ? " flips" : ""}">
      ${link ? `<a class="g-link" href="${href.matchup(game.week, a.team.teamId)}" aria-label="${esc(label)}"></a>` : ""}
      ${team(a, "ta")}
      ${score(a, b, "sa")}
      <div class="g-vs"><span>vs</span>${flips ? '<span class="tag">flips</span>' : ""}</div>
      ${score(b, a, "sb")}
      ${team(b, "tb")}
    </div>`;
}

export const backLink = (to: string, text: string): string =>
  `<a class="back" href="${to}">← ${esc(text)}</a>`;
